import { produce } from 'immer';

import { workspaceActionTypes } from './workspace.types';

const initialState = {
    // All workspaces the user belongs to.
    // Each workspace: { id, title, slug, owner_id, pricing_plan, seats, seats_used, role, created_at, updated_at }
    list: [],

    // The currently active workspace object.
    currentWorkspace: null,

    // Teams inside the current workspace.
    // Each team: { id, workspace_id, name, members_count, created_at }
    teams: [],

    // Members inside the current workspace.
    // Each member: { id, user_id, workspace_id, role, email, name, image, joined_at }
    members: [],

    // Pending invites for the current workspace (owner/admin only).
    // Each invite: { id, email, role, status, expires_at, created_at, invited_by }
    invites: [],

    // Pricing plan key: 'free' | 'basic' | 'professional'
    plan: 'free',

    // Computed restrictions object derived from plan (see workspace.plans.js).
    restrictions: null,

    // True once the first workspace fetch completes (success or failure).
    isInitialized: false,

    isLoading: false,
    error: null,
    pendingSwitchId: null,
};

const workspaceReducer = (state = initialState, action) => {
    return produce(state, (draft) => {
        switch (action.type) {

            // INIT
            case workspaceActionTypes.WORKSPACE_INIT_REQUEST: {
                draft.isLoading = true;
                draft.error = null;
                break;
            }
            case workspaceActionTypes.WORKSPACE_INIT_SUCCESS: {
                const { list, currentWorkspace, plan, restrictions } = action.payload;
                draft.list = list;
                draft.currentWorkspace = currentWorkspace;
                draft.plan = plan;
                draft.restrictions = restrictions;
                draft.isLoading = false;
                draft.isInitialized = true;
                draft.error = null;
                break;
            }
            case workspaceActionTypes.WORKSPACE_INIT_FAILURE: {
                draft.isLoading = false;
                // Mark as initialized even on failure so the app never hangs.
                draft.isInitialized = true;
                draft.error = action.payload;
                break;
            }

            // SWITCH WORKSPACE
            case workspaceActionTypes.WORKSPACE_SWITCH_REQUEST: {
                draft.isLoading = true;
                draft.pendingSwitchId = action.payload;
                break;
            }
            case workspaceActionTypes.WORKSPACE_SWITCH_SUCCESS: {
                const { currentWorkspace, plan, restrictions } = action.payload;
                draft.currentWorkspace = currentWorkspace;
                draft.plan = plan;
                draft.restrictions = restrictions;
                // Clear stale team/member/invite data from the previous workspace.
                draft.teams = [];
                draft.members = [];
                draft.invites = [];
                draft.isLoading = false;
                draft.pendingSwitchId = null;
                break;
            }
            case workspaceActionTypes.WORKSPACE_SWITCH_FAILURE: {
                draft.isLoading = false;
                draft.error = action.payload;
                draft.pendingSwitchId = null;
                break;
            }

            // FETCH TEAMS
            case workspaceActionTypes.WORKSPACE_FETCH_TEAMS_REQUEST: {
                draft.isLoading = true;
                break;
            }
            case workspaceActionTypes.WORKSPACE_FETCH_TEAMS_SUCCESS: {
                draft.teams = action.payload;
                draft.isLoading = false;
                break;
            }
            case workspaceActionTypes.WORKSPACE_FETCH_TEAMS_FAILURE: {
                draft.isLoading = false;
                draft.error = action.payload;
                break;
            }

            // FETCH MEMBERS
            case workspaceActionTypes.WORKSPACE_FETCH_MEMBERS_REQUEST: {
                draft.isLoading = true;
                break;
            }
            case workspaceActionTypes.WORKSPACE_FETCH_MEMBERS_SUCCESS: {
                draft.members = action.payload;
                draft.isLoading = false;
                break;
            }
            case workspaceActionTypes.WORKSPACE_FETCH_MEMBERS_FAILURE: {
                draft.isLoading = false;
                draft.error = action.payload;
                break;
            }

            // UPDATE WORKSPACE — merge changed fields into list + currentWorkspace.
            case workspaceActionTypes.WORKSPACE_UPDATE_SUCCESS: {
                const { id, fields } = action.payload;
                if (draft.currentWorkspace && draft.currentWorkspace.id === id) {
                    Object.assign(draft.currentWorkspace, fields);
                }
                const idx = draft.list.findIndex((w) => w.id === id);
                if (idx !== -1) {
                    Object.assign(draft.list[idx], fields);
                }
                break;
            }

            // LEAVE WORKSPACE — drop it from the list. currentWorkspace
            // reassignment is handled by the WORKSPACE_SWITCH_SUCCESS the saga
            // dispatches afterwards (or a full re-init when none remain).
            case workspaceActionTypes.WORKSPACE_LEAVE_SUCCESS: {
                const leftId = action.payload;
                draft.list = draft.list.filter((w) => w.id !== leftId);
                break;
            }

            // ARCHIVE WORKSPACE — drop it from the active list (same as leave).
            // currentWorkspace reassignment is handled by WORKSPACE_SWITCH_SUCCESS.
            case workspaceActionTypes.WORKSPACE_ARCHIVE_SUCCESS: {
                const archivedId = action.payload;
                draft.list = draft.list.filter((w) => w.id !== archivedId);
                break;
            }

            // REMOVE MEMBER — drop the membership row by the target's users.id
            // (the success payload is a users.id, matching the DELETE …/members/:userId call).
            case workspaceActionTypes.WORKSPACE_REMOVE_MEMBER_SUCCESS: {
                const userId = action.payload;
                draft.members = draft.members.filter((m) => m.user_id !== userId);
                break;
            }

            // FETCH INVITES — replace the pending-invites list.
            case workspaceActionTypes.WORKSPACE_FETCH_INVITES_SUCCESS: {
                draft.invites = action.payload || [];
                break;
            }

            // REVOKE INVITE — drop the invite row by id.
            case workspaceActionTypes.WORKSPACE_REVOKE_INVITE_SUCCESS: {
                const inviteId = action.payload;
                draft.invites = draft.invites.filter((i) => i.id !== inviteId);
                break;
            }

            // RESET (sign out)
            case workspaceActionTypes.WORKSPACE_RESET: {
                return initialState;
            }

            default:
                break;
        }
    });
};

export default workspaceReducer;
